import { memo, useMemo, useCallback, useEffect, useState, createRef } from 'react';
import { View, StyleSheet, Text, Pressable, Image } from 'react-native';

import {
  I18n,
  Navigation,
  NavBar,
  Container,
  Utils,
  DateUtils,
  FormFooter,
  StyledButton,
  Select,
} from '../../components';
import { THEME, FONT, COLOR, __WEB__, SELECT_STYLE_ROUND } from '../../../constants/styles';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';
import { CALENDAR_UPCOMING_ON_CALL, CALENDAR_ON_CALL } from '../../../notificationsHandlersTypes';

const SELECT_STYLE_ROUND_SHORT = {
  ...SELECT_STYLE_ROUND,
  menuList: (base, state) => ({
    ...base,
    zIndex: 9999,
    height: 130,
  }),
};

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import momentTz from 'moment-timezone';

import { getCurrentEmployer } from '../../../selectors/entities';
import { getStaffByMedicalServiceAndClinicalLocation } from '../../staff/selector';
import { setMedicalServiceRoster, getMedicalServiceRoster, resetState } from '../../staff/actions';

function mapStateToProps(state, ownProps) {
  const userProfile = state.entities.userProfile;
  const staff = getStaffByMedicalServiceAndClinicalLocation(state);
  const currentEmployer = getCurrentEmployer(state);
  const medicalService = currentEmployer?.medicalService;
  const clinicalLocation = currentEmployer?.clinicalLocation;
  const optionskey = `${medicalService}_${clinicalLocation}`;
  const staffOptions = (staff?.[optionskey] || [])
    .map((s) => {
      return s.userId !== userProfile.userId
        ? {
            label: s.fullName,
            id: s.userId,
          }
        : null;
    })
    .filter((item) => item !== null);

  const resource = ownProps.notification?.resource;

  const ON_CALL_TEXT = 'You are on call';
  const UPCOMING_ONCALL_TEXT = 'You are up to be the On-Call Provider for:';

  // prioritize data that matches the notification resource, otherwise just show their
  // current or upcoming oncall status
  let onCallDate;
  let onCallText;
  let onCallDateUTC;
  if (resource === CALENDAR_ON_CALL && currentEmployer?.on_call) {
    onCallDate = new Date(currentEmployer?.on_call);
    onCallDateUTC = currentEmployer?.on_call;
    onCallText = ON_CALL_TEXT;
  } else if (resource === CALENDAR_UPCOMING_ON_CALL && currentEmployer?.upcoming_on_call) {
    onCallDate = new Date(currentEmployer?.upcoming_on_call);
    onCallDateUTC = currentEmployer?.upcoming_on_call;
    onCallText = UPCOMING_ONCALL_TEXT;
  } else if (currentEmployer?.on_call) {
    onCallDate = new Date(currentEmployer?.on_call);
    onCallDateUTC = currentEmployer?.on_call;
    onCallText = ON_CALL_TEXT;
  } else if (currentEmployer?.upcoming_on_call) {
    onCallDate = new Date(currentEmployer?.upcoming_on_call);
    onCallDateUTC = currentEmployer?.upcoming_on_call;
    onCallText = UPCOMING_ONCALL_TEXT;
  } else {
    onCallDate = null;
    onCallDateUTC = '';
    onCallText = null;
  }

  // if the upcoming call is too far in the future show not on call message
  let onCallDateMoment = (onCallDate && momentTz(onCallDate).tz(currentEmployer?.timeZone)) || null;
  if (onCallDateMoment?.isAfter(momentTz().add(23, 'hours'))) {
    onCallDateMoment = null;
    onCallText = null;
  }
  // onCallDateMoment = null;

  const monthDate = onCallDateMoment?.format('YYYY-MM-DD') || null;

  let roster;
  if (monthDate) {
    const fullRoster = state.screens.staff.medicalServiceRoster || [];
    const rosterKey = Object.keys(fullRoster).find((k) => {
      const r = fullRoster[k];
      return r.primary_user_id === userProfile.userId && r.start_date === onCallDateUTC;
    });
    roster = fullRoster[rosterKey];
  }

  return {
    userProfile,
    currentEmployer,
    status: state.screens.staff.status,
    ownUserStatus: state.screens.staff.ownUserStatus,
    errorMessage: state.screens.staff.errorMessage,
    staffUpdateStatus: state.screens.staff.staffUpdateStatus,
    staffUpdateErrorMessage: state.screens.staff.staffUpdateErrorMessage,
    staffOptions,
    roster,
    monthDate,
    onCallDateMoment,
    onCallText,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        getMedicalServiceRoster,
        setMedicalServiceRoster,
        resetState,
      },
      dispatch
    ),
  };
}

export default memo(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )((props) => {
    useEffect(function () {
      return function () {
        props.onClose?.();
      };
    }, []);

    const [reassign, setReassign] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(false);

    useEffect(
      function () {
        if (props.staffUpdateStatus) {
          setSelectedStaff(null);
          setReassign(null);
        }
      },
      [props.staffUpdateStatus]
    );

    function handleOk() {
      props.onConfirm?.();
      Navigation.dismissAllModals();
    }
    function toggleReassign() {
      if (!reassign) {
        props.actions.getMedicalServiceRoster(props.monthDate);
      }
      setReassign(!reassign);
    }
    function handleSelectStaff(val) {
      setSelectedStaff(val);
    }
    function submitReasignment() {
      const newRoster = {
        ...props.roster,
        primary_user_id: selectedStaff.id,
      };
      if (newRoster.secondary_user_id === selectedStaff.id) newRoster.secondary_user_id = null;
      if (newRoster.alternate_user_id === selectedStaff.id) newRoster.alternate_user_id = null;

      props.actions.setMedicalServiceRoster({ items: [newRoster] });
    }

    const reassignBtnDisabled = reassign ? !selectedStaff : false;
    const footer = (
      <View style={styles.footer}>
        {!reassign ? (
          <Pressable style={styles.acceptBtn} onPress={handleOk}>
            <Image
              source={require('../../../../resources/icon-pager-small.png')}
              style={styles.pagerIcon}
            />
            <Text style={[styles.acceptBtnText, { marginLeft: 10 }]}>Ok</Text>
          </Pressable>
        ) : (
          <Pressable style={styles.cancelBtn} onPress={toggleReassign}>
            <Text style={styles.cancelBtnText}>Cancel</Text>
          </Pressable>
        )}
        <View style={{ flex: 1 }} />
        {!!props.onCallDateMoment && (
          <Pressable
            style={[
              reassign ? styles.acceptBtn : styles.rejectBtn,
              reassignBtnDisabled ? THEME.disabled : null,
            ]}
            onPress={reassign ? submitReasignment : toggleReassign}
            disabled={reassignBtnDisabled}
          >
            <Text style={styles.reassignBtnText}>Re-Assign</Text>
          </Pressable>
        )}
      </View>
    );
    return (
      <Container
        renderActivityOverlay={
          props.status === REQUESTING ||
          props.ownUserStatus === REQUESTING ||
          props.staffUpdateStatus === REQUESTING
        }
        errorMessage={props.errorMessage?.message || props.staffUpdateErrorMessage?.message}
        scrollView={false}
      >
        <View
          style={[
            { justifyContent: 'center', alignItems: 'center' },
            reassign ? { paddingVertical: 25 } : { flex: 1 },
          ]}
        >
          {!props.onCallDateMoment ? (
            <View style={{ width: 400 }}>
              <Text style={[styles.textRegular, { marginBottom: 25, fontSize: 18 }]}>
                You are not scheduled to be on-call within the next day.
              </Text>
            </View>
          ) : (
            <View style={{ width: 400 }}>
              <Text style={[styles.textRegular, { marginBottom: 25, fontSize: 18 }]}>
                {props.onCallText}
              </Text>
              <Text style={[styles.textBold, { marginBottom: 5 }]}>
                {props.currentEmployer?.clinicalLocation}
              </Text>
              <Text style={[styles.textBold, { marginBottom: 5 }]}>
                {props.currentEmployer?.medicalService}
              </Text>
              <Text style={[styles.textBold, { marginBottom: 5 }]}>
                On-Call Service:{' '}
                <Text style={styles.textRegular}>
                  {props.onCallDateMoment?.format('MMMM DD, YYYY - HH:mm')}
                </Text>
              </Text>
            </View>
          )}
        </View>
        {!!reassign && (
          <View style={{ flex: 1, zIndex: 1000 }}>
            <View style={styles.bottomContainerA}>
              <Text style={[styles.textRegular, { width: 400, marginTop: 0 }]}>
                Re-assign On-Call duty to:
              </Text>
            </View>
            <View style={styles.bottomContainerB}>
              <View style={[THEME.basicRow, { width: 400, zIndex: 1000 }]}>
                <Text style={styles.textMedium}>Primary: </Text>
                <View style={[styles.selectContainer]}>
                  <Select
                    onValue={handleSelectStaff}
                    required={false}
                    closeOnSelect={true}
                    options={props.staffOptions}
                    label={null}
                    value={selectedStaff || null}
                    webMode={true}
                    isClearable={false}
                    containerStyle={{ marginVertical: 0 }}
                    noLabel={true}
                    styles={SELECT_STYLE_ROUND_SHORT}
                  />
                </View>
              </View>
            </View>
          </View>
        )}
        {footer}
      </Container>
    );
  })
);

const styles = StyleSheet.create({
  bottomContainerA: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
    zIndex: 1000,
  },
  bottomContainerB: {
    ...THEME.borderTop,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
    zIndex: 1000,
  },
  selectContainer: {
    flex: 1,
    height: 60,
    paddingLeft: 7,
    zIndex: 1000,
  },
  textRegular: {
    ...FONT.regular,
    color: COLOR.text,
    fontSize: 16,
  },
  textBold: {
    ...FONT.bold,
    color: COLOR.text,
    fontSize: 16,
  },
  textMedium: {
    ...FONT.medium,
    color: COLOR.text,
    fontSize: 16,
  },
  footer: {
    ...THEME.basicRow,
    ...THEME.borderTop,
    paddingHorizontal: 30,
    paddingVertical: 30,
    backgroundColor: COLOR.altContrast,
  },
  acceptBtn: {
    backgroundColor: COLOR.greenColor,
    paddingVertical: 20,
    paddingHorizontal: 35,
    borderRadius: 5,
    flexDirection: 'row',
  },
  acceptBtnText: {
    ...FONT.medium,
    color: 'white',
    fontSize: 16,
  },
  reassignBtnText: {
    ...FONT.regular,
    color: 'white',
    fontSize: 16,
  },
  rejectBtn: {
    backgroundColor: '#f05150',
    paddingVertical: 20,
    paddingHorizontal: 35,
    borderRadius: 5,
  },
  cancelBtn: {
    backgroundColor: COLOR.contrast,
    borderWidth: 1.5,
    borderColor: '#ddd',
    paddingVertical: 18,
    paddingHorizontal: 35,
    borderRadius: 5,
  },
  cancelBtnText: {
    ...FONT.regular,
    color: COLOR.text,
    fontSize: 16,
  },
  pagerIcon: {
    width: 30,
    height: 21,
  },
});
